/**
 * 8.1 — Cohortes et rétention EXACTE.
 *
 * ⚠️ « Exacte » veut dire : le compte a agi CE jour-là précisément. C'est une
 * mesure bien plus dure que celle de l'onglet Rétention (« ce jour-là ou
 * après »), et les deux ne se comparent pas. Un retour exact à J+7 de 15 % ne
 * contredit pas un taux cumulé de 68 % : le premier dit « ils jouent le
 * septième jour », le second « ils sont encore là après une semaine ».
 *
 * Les trois horizons sont ORDINAUX — J+1 avant J+7 avant J+14 — d'où trois pas
 * de la rampe séquentielle plutôt que trois teintes distinctes.
 */

import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { MIN_GROUP_SIZE } from '@shared/types/stats';

import { formatDayShort, formatInteger, formatMonthShort, formatPercent } from '../../lib/format';
import { AXIS_STYLE, BAR_MAX_SIZE, COLORS, SEQUENTIAL_RAMP } from '../../lib/theme';
import type { CohortAnalysis, CohortRow, DataColumn } from '../../types';
import { ChartCard } from '../Card';
import { ChartLegend, ChartTooltip, type LegendEntry } from '../ChartTooltip';
import { ReadingBox } from '../ReadingBox';

// Trois pas NON consecutifs de la rampe : deux crans d'ecart de clarte entre
// chacun, donc distinguables cote a cote.
const SERIES = [
  { key: 'rateD1', label: 'J+1', color: SEQUENTIAL_RAMP[1] as string },
  { key: 'rateD7', label: 'J+7', color: SEQUENTIAL_RAMP[3] as string },
  { key: 'rateD14', label: 'J+14', color: SEQUENTIAL_RAMP[5] as string },
];

const legend: readonly LegendEntry[] = SERIES.map((series) => ({
  label: series.label,
  color: series.color,
  shape: 'area',
}));

const dash = (value: number | null) => (value === null ? '—' : formatPercent(value));

/**
 * Libellé de cohorte.
 *
 * Les cohortes mensuelles portent l'année : sur une période de plus d'un an,
 * deux mois homonymes s'affichaient tous deux « 01 sept. » et devenaient
 * indiscernables sur l'axe. Les cohortes hebdomadaires n'ont pas ce problème —
 * la bascule au mois intervient justement avant vingt semaines.
 */
const cohortLabel = (granularity: 'week' | 'month') =>
  granularity === 'month' ? formatMonthShort : formatDayShort;

const makeColumns = (granularity: 'week' | 'month'): ReadonlyArray<DataColumn<CohortRow>> => [
  { key: 'cohort', header: 'Cohorte', render: (row) => cohortLabel(granularity)(row.cohort) },
  { key: 'total', header: 'Comptes', numeric: true, render: (row) => formatInteger(row.totalUsers) },
  { key: 'd1', header: 'J+1', numeric: true, render: (row) => dash(row.rateD1) },
  { key: 'e7', header: 'Élig. J+7', numeric: true, render: (row) => formatInteger(row.eligibleD7) },
  { key: 'd7', header: 'J+7', numeric: true, render: (row) => dash(row.rateD7) },
  { key: 'e14', header: 'Élig. J+14', numeric: true, render: (row) => formatInteger(row.eligibleD14) },
  { key: 'd14', header: 'J+14', numeric: true, render: (row) => dash(row.rateD14) },
];

/**
 * Tendance sur le retour exact à J+7.
 *
 * Comparaison des deux moitiés de la période plutôt qu'une régression : avec
 * une dizaine de points bruités, une pente donnerait une fausse impression de
 * précision. Sous deux points d'écart, on dit « stable ».
 */
function trend(cohorts: readonly CohortRow[]): string {
  const rates = cohorts.map((cohort) => cohort.rateD7).filter((rate): rate is number => rate !== null);
  if (rates.length < 4) return 'Trop peu de cohortes publiables pour dégager une tendance.';

  const half = Math.floor(rates.length / 2);
  const first = rates.slice(0, half).reduce((sum, rate) => sum + rate, 0) / half;
  const last = rates.slice(-half).reduce((sum, rate) => sum + rate, 0) / half;
  const delta = last - first;

  if (Math.abs(delta) < 2) return "Le retour exact à J+7 est stable d'une cohorte à l'autre.";
  return delta > 0
    ? `Le retour exact à J+7 s'améliore : ${formatPercent(Math.abs(delta))} de plus sur la seconde moitié de la période.`
    : `Le retour exact à J+7 se dégrade : ${formatPercent(Math.abs(delta))} de moins sur la seconde moitié de la période.`;
}

export function CohortsPanel({ data }: { data: CohortAnalysis }) {
  const rows = data.cohorts;
  const granularityLabel = data.granularity === 'week' ? 'hebdomadaires' : 'mensuelles';
  const label = cohortLabel(data.granularity);
  const columns = makeColumns(data.granularity);

  const published = rows.filter((row) => row.rateD7 !== null);
  const best = [...published].sort((a, b) => (b.rateD7 ?? 0) - (a.rateD7 ?? 0))[0];
  const worst = [...published].sort((a, b) => (a.rateD7 ?? 0) - (b.rateD7 ?? 0))[0];

  return (
    <>
      <ReadingBox
        method={
          <>
            Rétention <strong>exacte</strong> : le compte a agi ce jour-là précisément, pas « ce
            jour-là ou après ». Une cohorte dont aucun jalon n'atteint {MIN_GROUP_SIZE} comptes
            éligibles n'est pas publiée
            {data.skippedCohorts > 0
              ? ` (${formatInteger(data.skippedCohorts)} écartée${data.skippedCohorts > 1 ? 's' : ''})`
              : ''}
            . Cohortes {granularityLabel} — la bascule au mois est automatique au-delà de vingt
            semaines.
          </>
        }
      >
        {rows.length === 0
          ? 'Aucune cohorte ne compte assez de comptes pour être publiée sur cette période.'
          : trend(rows)}
        {best && worst && best.cohort !== worst.cohort ? (
          <>
            {' '}
            Meilleure cohorte : {label(best.cohort)} à {dash(best.rateD7)} ; la plus faible,{' '}
            {label(worst.cohort)} à {dash(worst.rateD7)}.
          </>
        ) : null}
      </ReadingBox>

      <div className="chart-grid">
        <div className="chart-grid__wide">
          <ChartCard
            title="Retour exact par cohorte"
            subtitle="Part des comptes ayant agi précisément à J+1, J+7 et J+14"
            emptyMessage="Aucune cohorte publiable sur cette période."
            rows={rows}
            columns={columns}
            chartHeight={330}
          >
            <ChartLegend entries={legend} />
            <ResponsiveContainer width="100%" height="88%">
              <BarChart data={rows as CohortRow[]} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid stroke={COLORS.grid} vertical={false} />
                <XAxis
                  dataKey="cohort"
                  tickFormatter={label}
                  tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
                  axisLine={{ stroke: COLORS.grid }}
                  tickLine={false}
                  minTickGap={24}
                />
                <YAxis
                  tickFormatter={(value: number) => `${value} %`}
                  tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
                  axisLine={false}
                  tickLine={false}
                  width={52}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                  content={
                    <ChartTooltip labelFormatter={label} valueFormatter={formatPercent} />
                  }
                />
                {SERIES.map((series) => (
                  <Bar
                    key={series.key}
                    isAnimationActive={false}
                    dataKey={series.key}
                    name={series.label}
                    fill={series.color}
                    radius={[3, 3, 0, 0]}
                    maxBarSize={BAR_MAX_SIZE}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>
    </>
  );
}
